import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Users, LogOut, Loader2, LayoutDashboard, FolderOpen, Inbox, CalendarDays, UserCog, ClipboardList, Sparkles, Table2, Wallet, Wand2, Banknote, TrendingUp, FileText, Briefcase } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { LOGO_IMG } from "@/portal/components/adminShared";
import ProfitabilityTab from "@/portal/components/ProfitabilityTab";
import ClientsTab from "@/portal/components/ClientsTab";
import DashboardTab from "@/portal/components/DashboardTab";
import DocumentsTab from "@/portal/components/DocumentsTab";
import LeadsTab from "@/portal/components/LeadsTab";
import CalendarTab from "@/portal/components/CalendarTab";
import TeamTab from "@/portal/components/TeamTab";
import TasksTab from "@/portal/components/TasksTab";
import AiTab from "@/portal/components/AiTab";
import TablesTab from "@/portal/components/TablesTab";
import ExpensesTab from "@/portal/components/ExpensesTab";
import PhotoStudioTab from "@/portal/components/PhotoStudioTab";
import VehiclesAdminTab from "@/portal/components/VehiclesAdminTab";
import PayrollTab from "@/payroll/PayrollTab";
import FakturyTab from "@/portal/components/FakturyTab";
import VehicleRegistryTab from "@/portal/components/VehicleRegistryTab";
import CareersTab from "@/portal/components/CareersTab";

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const isAccountant = user?.role === "accountant";
  const [tab, setTab] = useState(isAccountant ? "invoices" : "dashboard");
  const [cars, setCars] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const [c, cl] = await Promise.all([
        apiFetch("/admin/cars", { token }),
        apiFetch("/admin/clients", { token }),
      ]);
      setCars(c);
      setClients(cl);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  // Účetní má přístup jen k finančním sekcím – kdyby se stav nastavil dřív než doběhne user.
  useEffect(() => {
    if (isAccountant && !["invoices", "evidence", "expenses", "payroll", "profit"].includes(tab)) setTab("invoices");
  }, [isAccountant, tab]);

  const handleLogout = () => {
    logout();
    navigate("/prihlaseni", { replace: true });
  };

  const isAdmin = user?.role === "admin";
  const tabs = isAccountant ? [
    { key: "invoices", label: "Faktury", icon: FileText },
    { key: "evidence", label: "Vozidla", icon: Car },
    { key: "expenses", label: "Náklady", icon: Wallet },
    { key: "payroll", label: "Výplaty", icon: Banknote },
    { key: "profit", label: "Ziskovost", icon: TrendingUp },
  ] : [
    { key: "dashboard", label: "Přehled", icon: LayoutDashboard },
    { key: "leads", label: "Poptávky", icon: Inbox },
    { key: "calendar", label: "Kalendář", icon: CalendarDays },
    { key: "tasks", label: "Úkoly", icon: ClipboardList },
    { key: "clients", label: "Klienti & pronájmy", icon: Users },
    { key: "documents", label: "Dokumenty", icon: FolderOpen },
    { key: "careers", label: "Kariéra", icon: Briefcase },
    { key: "ai", label: "AI asistent", icon: Sparkles },
    { key: "photo", label: "Foto studio", icon: Wand2 },
    { key: "vehicles", label: "Vozy na webu", icon: Car },
    { key: "payroll", label: "Výplaty", icon: Banknote },
    ...(isAdmin ? [{ key: "invoices", label: "Faktury", icon: FileText }, { key: "tables", label: "Tabulky", icon: Table2 }, { key: "expenses", label: "Náklady", icon: Wallet }, { key: "profit", label: "Ziskovost", icon: TrendingUp }, { key: "team", label: "Tým", icon: UserCog }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_IMG} alt="EffectDeal" className="w-9 h-9 object-contain" />
            <div>
              <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide block leading-tight">EFFECTDEAL</span>
              <span className="text-xs text-zinc-500">Administrace</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {!isAccountant && (
              <a href="/flotila" className="text-sm font-medium px-3 py-1.5 rounded-full bg-orange-500/15 text-orange-300 hover:bg-orange-500/25 inline-flex items-center gap-1.5" data-testid="open-flotila-link" title="Vozový park a flotila – evidence, přidávání a správa všech vozů">
                <Car className="w-4 h-4" /> Vozový park
              </a>
            )}
            <span className="hidden sm:inline text-sm text-zinc-500">{user?.email}</span>
            <button onClick={handleLogout} className="text-sm text-zinc-400 hover:text-white inline-flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Odhlásit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Tabs */}
        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto mb-8">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 whitespace-nowrap">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium rounded-full inline-flex items-center gap-2 transition-colors ${tab === t.key ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-orange-400 animate-spin" /></div>
        ) : tab === "leads" ? (
          <LeadsTab token={token} />
        ) : tab === "calendar" ? (
          <CalendarTab token={token} />
        ) : tab === "tasks" ? (
          <TasksTab token={token} />
        ) : tab === "ai" ? (
          <AiTab token={token} />
        ) : tab === "photo" ? (
          <PhotoStudioTab token={token} />
        ) : tab === "vehicles" ? (
          <VehiclesAdminTab token={token} />
        ) : tab === "evidence" ? (
          <VehicleRegistryTab token={token} />
        ) : tab === "careers" ? (
          <CareersTab token={token} />
        ) : tab === "tables" ? (
          <TablesTab clients={clients} />
        ) : tab === "payroll" ? (
          <PayrollTab token={token} role={user?.role} />
        ) : tab === "expenses" ? (
          <ExpensesTab cars={cars} token={token} />
        ) : tab === "invoices" ? (
          <FakturyTab token={token} />
        ) : tab === "profit" ? (
          <ProfitabilityTab token={token} />
        ) : tab === "team" ? (
          <TeamTab token={token} />
        ) : tab === "dashboard" ? (
          <DashboardTab clients={clients} cars={cars} token={token} onGoClients={() => setTab("clients")} />
        ) : tab === "documents" ? (
          <DocumentsTab clients={clients} token={token} />
        ) : (
          <ClientsTab clients={clients} cars={cars} token={token} onChange={reload} />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
